/**
 * Validate a single phone number.
 * Rules: only digits, 10-15 chars, no spaces/dashes/plus signs.
 * @returns {{ valid: boolean, phone: string|null, error: string|null }}
 */
export function parsePhoneNumber(raw) {
  const str = String(raw).trim();
  if (!str) return { valid: false, phone: null, error: 'Valor vacío' };

  const cleaned = str.replace(/[\s\-+()]/g, '');

  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, phone: null, error: 'Contiene caracteres no numéricos' };
  }
  if (cleaned.length < 10 || cleaned.length > 15) {
    return { valid: false, phone: null, error: `Longitud inválida (${cleaned.length} dígitos, esperado 10-15)` };
  }
  return { valid: true, phone: cleaned, error: null };
}

/** Common header words to skip when scanning rows. */
const HEADER_WORDS = new Set([
  'teléfono', 'telefono', 'phone', 'nombre', 'name',
  'categoría', 'categoria', 'category', 'email', 'correo',
]);

function isHeader(value) {
  return HEADER_WORDS.has(value.toLowerCase().replace(/[^a-záéíóúñ]/g, ''));
}

/**
 * Decode an ArrayBuffer to string.
 * Tries UTF-8 first (fatal), falls back to Latin-1.
 */
export function decodeText(buffer, preferredEncoding = 'utf-8') {
  try {
    return new TextDecoder(preferredEncoding, { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('iso-8859-1').decode(buffer);
  }
}

/** Lazy dynamic import of SheetJS – only loaded when an .xlsx is processed. */
let xlsxPromise = null;
function getXLSX() {
  if (!xlsxPromise) {
    xlsxPromise = import('xlsx').then((m) => m.default || m);
  }
  return xlsxPromise;
}

/**
 * Parse an .xlsx file and return raw candidate strings (first non-header cell per row).
 */
export async function parseXLSXFile(file) {
  const XLSX = await getXLSX();
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false, cellText: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  const candidates = [];
  for (const row of rows) {
    for (const cell of row) {
      const str = String(cell).trim();
      if (str && !isHeader(str)) {
        candidates.push(str);
        break;
      }
    }
  }
  return candidates;
}

/**
 * Parse a .csv file and return raw candidate strings (first non-header cell per row).
 * Handles comma, semicolon, and tab separators.
 */
export async function parseCSVFile(file) {
  const buffer = await file.arrayBuffer();
  const text = decodeText(buffer);
  const lines = text.split(/\r?\n/);

  // Detect separator from first line
  const firstLine = lines[0] || '';
  const separator = [',', ';', '\t'].find((s) => firstLine.includes(s)) || ',';

  const candidates = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const columns = trimmed.split(separator);
    for (const col of columns) {
      const cell = col.trim().replace(/^["']|["']$/g, '');
      if (cell && !isHeader(cell)) {
        candidates.push(cell);
        break;
      }
    }
  }
  return candidates;
}

/**
 * Parse a .txt file (one entry per line) and return raw candidate strings.
 */
export async function parseTXTFile(file) {
  const buffer = await file.arrayBuffer();
  const text = decodeText(buffer);
  const lines = text.split(/\r?\n/);

  const candidates = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed && !isHeader(trimmed)) {
      candidates.push(trimmed);
    }
  }
  return candidates;
}

/**
 * Deduplicate an array of phone strings.
 */
export function deduplicatePhones(phones) {
  return [...new Set(phones)];
}

/**
 * Generate a validated .txt file (UTF‑8 without BOM, one phone per line).
 */
export function generateValidatedFile(phones, originalFilename) {
  const content = phones.join('\n');
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const baseName = originalFilename.replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}_validated.txt`, { type: 'text/plain' });
}

/**
 * Validate a contact file end‑to‑end.
 *
 * @param {File} file - The uploaded file (.xlsx, .csv, or .txt)
 * @returns {Promise<{
 *   validPhones: string[],
 *   totalProcessed: number,
 *   invalidCount: number,
 *   duplicateCount: number,
 *   validatedFile: File,
 * }>}
 */
export async function validateContactFile(file) {
  const fileName = file.name.toLowerCase();

  let candidates;
  if (fileName.endsWith('.xlsx')) {
    candidates = await parseXLSXFile(file);
  } else if (fileName.endsWith('.csv')) {
    candidates = await parseCSVFile(file);
  } else if (fileName.endsWith('.txt')) {
    candidates = await parseTXTFile(file);
  } else {
    throw new Error(`Tipo de archivo no soportado: ${file.name}`);
  }

  const valid = [];
  let invalidCount = 0;

  for (const raw of candidates) {
    const result = parsePhoneNumber(raw);
    if (result.valid) {
      valid.push(result.phone);
    } else {
      invalidCount += 1;
    }
  }

  const beforeDedup = valid.length;
  const validPhones = deduplicatePhones(valid);
  const duplicateCount = beforeDedup - validPhones.length;

  const validatedFile = generateValidatedFile(validPhones, file.name);

  return {
    validPhones,
    totalProcessed: candidates.length,
    invalidCount,
    duplicateCount,
    validatedFile,
  };
}

/* ------------------------------------------------------------------ */
/*  Column-count pre-check (lightweight, no phone parsing)             */
/* ------------------------------------------------------------------ */

/**
 * Lightweight column-count check — reads only the first row to verify
 * the file has exactly 1 + templateVariables.length columns.
 * Only checks when templateVariables is non-empty.
 * For .txt files the check is skipped (single column — no variables).
 *
 * @param {File} file
 * @param {string[]} templateVariables — e.g. ['1', '2']
 * @throws {Error} if column count does not match expected
 */
export async function checkFileColumnCount(file, templateVariables) {
  if (templateVariables.length === 0) return;

  const fileName = file.name.toLowerCase();
  const expected = 1 + templateVariables.length;

  if (fileName.endsWith('.xlsx')) {
    const XLSX = await getXLSX();
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: false, cellText: true });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

    // Validar filas de datos (omitir header en row 0): contar columnas con contenido real
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.every((c) => String(c).trim() === '')) continue;
      const nonEmpty = trimTrailing(row.map((c) => String(c).trim())).length;
      if (nonEmpty !== expected) {
        throw new Error(
          `Fila ${i + 1}: tiene ${nonEmpty} columna(s) con datos, pero se requieren exactamente ${expected} ` +
          `(1 teléfono + ${templateVariables.length} variable(s))`
        );
      }
    }
  } else if (fileName.endsWith('.csv')) {
    const buffer = await file.arrayBuffer();
    const text = decodeText(buffer);
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length === 0) return;

    const sep = [',', ';', '\t'].find((s) => lines[0].includes(s)) || ',';

    // Validar filas de datos (omitir header en línea 0): contar columnas con contenido real
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(sep).map((c) => c.trim());
      const nonEmpty = trimTrailing(cols).length;
      if (nonEmpty !== expected) {
        throw new Error(
          `Fila ${i + 1}: tiene ${nonEmpty} columna(s) con datos, pero se requieren exactamente ${expected} ` +
          `(1 teléfono + ${templateVariables.length} variable(s))`
        );
      }
    }
  }
  // .txt — skip, variable columns not supported
}

/** Trim trailing empty strings from an array (avoids SheetJS column-range padding). */
function trimTrailing(arr) {
  let i = arr.length - 1;
  while (i >= 0 && arr[i] === '') i--;
  return i < arr.length - 1 ? arr.slice(0, i + 1) : arr;
}

/* ------------------------------------------------------------------ */
/*  Multi-column row parsers                                           */
/* ------------------------------------------------------------------ */

/**
 * Parse an .xlsx file returning all columns per row.
 * First row is treated as header and skipped.
 *
 * @param {File} file
 * @returns {Promise<{rows: string[][]}>}
 */
export async function parseXLSXFileRows(file) {
  const XLSX = await getXLSX();
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false, cellText: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  const rows = [];
  let isFirst = true;
  for (const raw of rawRows) {
    if (isFirst) {
      isFirst = false;
      continue;
    }
    const cells = raw.map((c) => String(c).trim());
    const trimmed = trimTrailing(cells);
    if (trimmed.every((c) => !c)) continue;
    rows.push(trimmed);
  }
  return { rows };
}

/**
 * Parse a .csv file returning all columns per row.
 * First row is treated as header and skipped.
 * Handles comma, semicolon, and tab separators.
 *
 * @param {File} file
 * @returns {Promise<{rows: string[][]}>}
 */
export async function parseCSVFileRows(file) {
  const buffer = await file.arrayBuffer();
  const text = decodeText(buffer);
  const lines = text.split(/\r?\n/);

  if (lines.length === 0) return { rows: [] };

  const firstLine = lines[0] || '';
  const separator = [',', ';', '\t'].find((s) => firstLine.includes(s)) || ',';

  const rows = [];
  let isFirst = true;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (isFirst) {
      isFirst = false;
      continue;
    }

    const columns = trimmed.split(separator).map((c) => c.trim().replace(/^["']|["']$/g, ''));
    if (columns.every((c) => !c)) continue;
    rows.push(columns);
  }
  return { rows };
}

/**
 * Generate a validated CSV file with phone + variable columns.
 *
 * @param {{phone: string, variables: string[]}[]} rows
 * @param {string[]} variableIndices — e.g. ['1', '2']
 * @param {string} originalFilename
 * @returns {File}
 */
export function generateValidatedCSV(rows, variableIndices, originalFilename) {
  const headers = ['phone', ...variableIndices.map((i) => `var_${i}`)];
  const lines = [headers.join(',')];

  for (const row of rows) {
    const cells = [row.phone, ...(row.variables || []).slice(0, variableIndices.length)];
    const escaped = cells.map((c) => {
      if (c.includes(',') || c.includes('"') || c.includes('\n')) {
        return `"${c.replace(/"/g, '""')}"`;
      }
      return c;
    });
    lines.push(escaped.join(','));
  }

  const content = lines.join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const baseName = originalFilename.replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}_validated.csv`, { type: 'text/csv' });
}

/**
 * Validate a contact file with optional template variable columns.
 *
 * When `templateVariables` is empty or not provided, behaves like the
 * existing validateContactFile (single-column, phone-only).
 *
 * When `templateVariables` is provided, validates that the file has at
 * least 1 + templateVariables.length columns and returns variable data
 * per contact.
 *
 * @param {File} file
 * @param {string[]} templateVariables — e.g. ['1', '2'] or []
 * @returns {Promise<{
 *   validPhones: string[],
 *   variableRows: {phone: string, variables: string[]}[],
 *   totalProcessed: number,
 *   invalidCount: number,
 *   duplicateCount: number,
 *   validatedFile: File,
 *   hasVariables: boolean,
 *   columnCount: number,
 *   expectedColumns: number,
 * }>}
 */
export async function validateContactFileWithVariables(file, templateVariables = []) {
  const fileName = file.name.toLowerCase();
  const hasVars = templateVariables.length > 0;

  let allRows = [];

  if (fileName.endsWith('.xlsx')) {
    const result = await parseXLSXFileRows(file);
    allRows = result.rows;
  } else if (fileName.endsWith('.csv')) {
    const result = await parseCSVFileRows(file);
    allRows = result.rows;
  } else if (fileName.endsWith('.txt')) {
    if (hasVars) {
      throw new Error('El archivo .txt no soporta columnas de variables. Use .csv o .xlsx.');
    }
    const candidates = await parseTXTFile(file);
    allRows = candidates.map((c) => [c]);
  } else {
    throw new Error(`Tipo de archivo no soportado: ${file.name}`);
  }

  const expectedColumns = 1 + templateVariables.length;

  if (hasVars && allRows.length > 0 && allRows[0].length !== expectedColumns) {
    const found = allRows[0].length;
    throw new Error(
      `El archivo tiene ${found} columna(s) pero el template requiere exactamente ${expectedColumns} ` +
      `(1 teléfono + ${templateVariables.length} variable(s): ${templateVariables.map((v) => `{{${v}}}`).join(', ')})`
    );
  }

  const variableRows = [];
  const valid = [];
  let invalidCount = 0;

  for (const rowColumns of allRows) {
    if (rowColumns.length === 0) continue;

    const phoneRaw = rowColumns[0];
    const phoneResult = parsePhoneNumber(phoneRaw);

    if (!phoneResult.valid) {
      invalidCount += 1;
      continue;
    }

    valid.push(phoneResult.phone);

    if (hasVars) {
      const vars = rowColumns.slice(1, expectedColumns);
      while (vars.length < templateVariables.length) {
        vars.push('');
      }
      variableRows.push({
        phone: phoneResult.phone,
        variables: vars,
      });
    }
  }

  const beforeDedup = valid.length;
  const validPhones = deduplicatePhones(valid);
  const duplicateCount = beforeDedup - validPhones.length;

  const seenPhones = new Set();
  const dedupedVariableRows = hasVars
    ? variableRows.filter((r) => {
        if (seenPhones.has(r.phone)) return false;
        seenPhones.add(r.phone);
        return true;
      })
    : [];

  const validatedFile = hasVars
    ? generateValidatedCSV(dedupedVariableRows, templateVariables, file.name)
    : generateValidatedFile(validPhones, file.name);

  return {
    validPhones,
    variableRows: dedupedVariableRows,
    totalProcessed: allRows.length,
    invalidCount,
    duplicateCount,
    validatedFile,
    hasVariables: hasVars,
    columnCount: allRows.length > 0 ? allRows[0].length : 0,
    expectedColumns,
  };
}
